import { useContext, useEffect, useState } from "react";
import { t } from "i18next";
import { HiOutlinePlus } from "react-icons/hi2";
import { TbMessage, TbTrash } from "react-icons/tb";
import { BsStars } from "react-icons/bs";
import { Button, Dropdown, Input } from "components/ui";
import capitalize from "components/ui/utils/capitalize";
import { AIContext } from "contexts/AIContext";
import { COMMENT_PROMPTS } from "configs/prompts/comments";
import { generateId } from "utils/generateId";
import ComposerField from "./ComposerField";

const MAX_COMMENTS = parseInt(process.env.REACT_APP_MAX_COMMENTS_PER_POST || "3");

/**
 * Plain list editor for `comments_to_create` (the comments published right
 * after the post goes live). Same persistence semantics as the preview's
 * CommentsSection: rows are kept locally until first edit ("saved") so an
 * empty new row is never sent to the API.
 */
const ComposerCommentsField = ({
    post,
    network,
    isEditable = true,
    onCommentsToCreateEdit
}) => {

    const { getByQueryChat } = useContext(AIContext);
    const [comments, setComments] = useState(
        post?.comments_to_create?.map(comment => ({ id: generateId(), text: comment, saved: true })) || []
    );
    const [isGeneratingAI, setIsGeneratingAI] = useState(false);

    // Resync saved rows when the post changes externally (e.g. refreshFromDb
    // after publishing). Edits made here round-trip identically, so this is a
    // no-op while typing — focus and unsaved drafts are preserved.
    const incomingCommentsKey = JSON.stringify(post?.comments_to_create || []);
    useEffect(() => {
        const incoming = JSON.parse(incomingCommentsKey);
        setComments(prev => {
            const localSaved = prev.filter(c => c.saved).map(c => c.text);
            if (incomingCommentsKey === JSON.stringify(localSaved)) return prev;
            return [
                ...incoming.map(text => ({ id: generateId(), text, saved: true })),
                ...prev.filter(c => !c.saved)
            ];
        });
    }, [incomingCommentsKey]);

    const persist = (newComments) => {
        onCommentsToCreateEdit(post.postIndex, newComments.filter(c => c.saved).map(c => c.text));
    };

    const handleAdd = () => {
        setComments([...comments, { id: generateId(), text: "", saved: false }]);
    };

    const handleUpdate = (id, text) => {
        const newComments = comments.map(c => c.id === id ? { ...c, text, saved: true } : c);
        setComments(newComments);
        persist(newComments);
    };

    const handleDelete = (id) => {
        const newComments = comments.filter(c => c.id !== id);
        setComments(newComments);
        persist(newComments);
    };

    const handleGenerateAI = async () => {
        setIsGeneratingAI(true);
        const prompt = COMMENT_PROMPTS[Math.floor(Math.random() * COMMENT_PROMPTS.length)];
        const query = `
            ${capitalize(network)} post content:
            ${post?.text || post?.description}

            ${post?.comments_to_create?.length > 0 ? `
                ${capitalize(network)} post previous comments:
                ${post?.comments_to_create?.map(comment => comment).join('\n')}
            ` : ``}

            ${prompt}

            No preambles. Just write the comment.
        `;
        try {
            const aiComment = await getByQueryChat(query);
            const newComments = [...comments, { id: generateId(), text: aiComment, saved: true }];
            setComments(newComments);
            persist(newComments);
        } catch (error) {
            console.error("Error generating AI comment:", error);
        }
        setIsGeneratingAI(false);
    };

    return (
        <ComposerField
            label={t('composer.commentsLabel', 'First comments')}
            hint={t('composer.commentsHelp', 'Posted automatically right after publishing.')}
            counter={
                <span className="text-xs tabular-nums text-tertiary-400 dark:text-gray-500">
                    {comments.length} / {MAX_COMMENTS}
                </span>
            }
        >
            <div className="flex flex-col gap-2">
                {comments.map((comment) => (
                    <div key={comment.id} className="flex items-start gap-2">
                        <Input
                            textArea
                            rows={2}
                            size="sm"
                            className="!rounded-lg"
                            value={comment.text}
                            disabled={!isEditable}
                            placeholder={t('composer.commentPlaceholder', 'Write a comment...')}
                            onChange={(e) => handleUpdate(comment.id, e.target.value)}
                        />
                        {isEditable && (
                            <Button
                                size="sm"
                                variant="plain"
                                className="!rounded-lg shrink-0 hover:!text-red-600"
                                icon={<TbTrash />}
                                onClick={() => handleDelete(comment.id)}
                            />
                        )}
                    </div>
                ))}
                {isEditable && comments.length < MAX_COMMENTS && (
                    <div className="flex">
                        <Dropdown
                            placement="bottom-start"
                            parentClassName="!shrink-0"
                            portal={true}
                            renderTitle={
                                <Button
                                    size="xs"
                                    variant="twoTone"
                                    className="!rounded-lg"
                                    icon={<TbMessage />}
                                    loading={isGeneratingAI}
                                >
                                    {comments.length === 0
                                        ? t('composer.addFirstComment', 'Add first comment')
                                        : t('composer.addAnotherComment', 'Add another comment')
                                    }
                                </Button>
                            }
                        >
                            <Dropdown.Item onClick={handleAdd} className="flex items-center gap-2 py-2 px-4">
                                <HiOutlinePlus className="text-lg" />
                                <span>{t('composer.writeFromScratch', 'Write from scratch')}</span>
                            </Dropdown.Item>
                            <Dropdown.Item onClick={handleGenerateAI} className="flex items-center gap-2 py-2 px-4">
                                <BsStars className="text-lg text-fuchsia-500" />
                                <span>{t('composer.generateWithAI', 'Generate with AI')}</span>
                            </Dropdown.Item>
                        </Dropdown>
                    </div>
                )}
            </div>
        </ComposerField>
    );
};

export default ComposerCommentsField;
